/**
 * Whole-map ownership at a glance, plus where you are looking, plus a way to
 * get somewhere else.
 *
 * Two things are load-bearing here.
 *
 * **It uses `hexToPixel`.** The minimap is a scaled-down copy of the main view's
 * projection, not a second one: the same pointy-top axial math at a smaller
 * `size`. Inventing a separate row/column layout would put the minimap subtly
 * out of register with the map it claims to summarise, and every click-to-jump
 * would land slightly wrong.
 *
 * **Ownership is redrawn on change, not per update.** The territory layer is
 * painted into an offscreen canvas and only repainted when a hex actually
 * changes hands; each update blits that and strokes the viewport rectangle over
 * it. Repainting 1600 hexes ten times a second to move a rectangle would cost
 * more than the rest of the HUD put together.
 */

import type { KingdomId, KingdomState, MapState, Point } from "kngdm-shared";
import { SQRT3, hexToPixel } from "kngdm-shared";

import { BIOME_COLORS, blendColors } from "../render/palette";
import { formatColor } from "./format";

export interface MinimapCallbacks {
  onJumpToWorld(point: { x: number; y: number }): void;
}

export interface MinimapView {
  readonly map: MapState;
  readonly kingdoms: readonly KingdomState[];
  readonly ownKingdomId: KingdomId;
  /** The main view's hex size, so mini-space converts back to world-space. */
  readonly hexSize: number;
  readonly cameraRect: { topLeft: Point; bottomRight: Point };
}

/** CSS pixels. The 40x40 map's pixel bounds are slightly wider than tall. */
const WIDTH = 184;
const HEIGHT = 158;
const PADDING = 4;

/** Backing-store scale. Past 2x the extra pixels are invisible and not free. */
const MAX_PIXEL_RATIO = 2;

/** Unclaimed land is pushed back toward the panel colour so territory reads forward. */
const UNCLAIMED_BLEND = 0.55;
const PANEL_INK = 0x14161c;

/** Your own territory is lifted toward white — "which one am I" in one glance. */
const OWN_LIFT = 0.22;
const OWN_LIFT_TARGET = 0xffffff;

const VIEWPORT_STROKE = "#f4f1e8";

interface Projection {
  /** Hex circumradius in mini-space. */
  readonly size: number;
  readonly offsetX: number;
  readonly offsetY: number;
}

export class Minimap {
  readonly element: HTMLElement;

  private readonly callbacks: MinimapCallbacks;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly layer: HTMLCanvasElement;
  private readonly layerCtx: CanvasRenderingContext2D | null;
  private readonly pixelRatio: number;

  private projection: Projection | null = null;
  private projectionKey = "";
  private ownershipHash = Number.NaN;
  private dragging = false;

  /** The main view's hex size, remembered so a click can leave mini-space. */
  private hexSize: number | null = null;

  constructor(callbacks: MinimapCallbacks) {
    this.callbacks = callbacks;
    this.pixelRatio = Math.min(MAX_PIXEL_RATIO, Math.max(1, window.devicePixelRatio || 1));

    this.element = document.createElement("section");
    this.element.className = "panel panel--minimap";

    this.canvas = document.createElement("canvas");
    this.canvas.className = "minimap__canvas";
    this.canvas.width = Math.round(WIDTH * this.pixelRatio);
    this.canvas.height = Math.round(HEIGHT * this.pixelRatio);
    this.canvas.style.width = `${WIDTH}px`;
    this.canvas.style.height = `${HEIGHT}px`;
    this.canvas.title = "Click to move the camera";

    this.layer = document.createElement("canvas");
    this.layer.width = this.canvas.width;
    this.layer.height = this.canvas.height;

    this.ctx = this.canvas.getContext("2d");
    this.layerCtx = this.layer.getContext("2d");
    this.ctx?.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
    this.layerCtx?.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);

    this.element.append(this.canvas);

    this.canvas.addEventListener("pointerdown", this.handlePointerDown);
    this.canvas.addEventListener("pointermove", this.handlePointerMove);
    this.canvas.addEventListener("pointerup", this.handlePointerUp);
    this.canvas.addEventListener("pointercancel", this.handlePointerUp);
    // The map underneath must not also receive this gesture as a claim.
    this.canvas.addEventListener("contextmenu", preventDefault);
  }

  update(view: MinimapView): void {
    const ctx = this.ctx;
    if (ctx === null) return;

    this.hexSize = view.hexSize;
    this.ensureProjection(view.map);
    const projection = this.projection;
    if (projection === null) return;

    const hash = ownershipHash(view.map, view.kingdoms);
    if (hash !== this.ownershipHash) {
      this.ownershipHash = hash;
      this.paintOwnership(view, projection);
    }

    ctx.clearRect(0, 0, WIDTH, HEIGHT);
    ctx.drawImage(this.layer, 0, 0, WIDTH, HEIGHT);
    this.strokeViewport(ctx, view, projection);
  }

  destroy(): void {
    this.canvas.removeEventListener("pointerdown", this.handlePointerDown);
    this.canvas.removeEventListener("pointermove", this.handlePointerMove);
    this.canvas.removeEventListener("pointerup", this.handlePointerUp);
    this.canvas.removeEventListener("pointercancel", this.handlePointerUp);
    this.canvas.removeEventListener("contextmenu", preventDefault);
    this.element.remove();
  }

  // -------------------------------------------------------------------------
  // Painting
  // -------------------------------------------------------------------------

  private paintOwnership(view: MinimapView, projection: Projection): void {
    const ctx = this.layerCtx;
    if (ctx === null) return;

    ctx.clearRect(0, 0, WIDTH, HEIGHT);

    const colors = new Map<KingdomId, string>();
    for (const kingdom of view.kingdoms) {
      const base =
        kingdom.id === view.ownKingdomId
          ? blendColors(kingdom.color, OWN_LIFT_TARGET, OWN_LIFT)
          : kingdom.color;
      colors.set(kingdom.id, formatColor(base));
    }

    // Row spacing is 1.5*size and column spacing sqrt(3)*size, so rects of
    // exactly those dimensions tile the plane. The half-pixel of slop closes
    // the seams that sub-pixel positions would otherwise leave between them.
    const cellWidth = SQRT3 * projection.size + 0.5;
    const cellHeight = 1.5 * projection.size + 0.5;

    for (const hex of view.map.hexes) {
      const fill =
        hex.owner === null
          ? formatColor(blendColors(BIOME_COLORS[hex.biome], PANEL_INK, UNCLAIMED_BLEND))
          : colors.get(hex.owner) ?? formatColor(PANEL_INK);

      const centre = hexToPixel(hex.coord, projection.size);
      ctx.fillStyle = fill;
      ctx.fillRect(
        centre.x + projection.offsetX - cellWidth / 2,
        centre.y + projection.offsetY - cellHeight / 2,
        cellWidth,
        cellHeight,
      );
    }
  }

  private strokeViewport(
    ctx: CanvasRenderingContext2D,
    view: MinimapView,
    projection: Projection,
  ): void {
    const scale = projection.size / view.hexSize;
    const left = view.cameraRect.topLeft.x * scale + projection.offsetX;
    const top = view.cameraRect.topLeft.y * scale + projection.offsetY;
    const right = view.cameraRect.bottomRight.x * scale + projection.offsetX;
    const bottom = view.cameraRect.bottomRight.y * scale + projection.offsetY;

    // Clamped so a zoomed-out camera looking past the map edge still shows a
    // rectangle rather than one drawn entirely off-panel.
    const x0 = clampTo(left, 0, WIDTH);
    const y0 = clampTo(top, 0, HEIGHT);
    const x1 = clampTo(right, 0, WIDTH);
    const y1 = clampTo(bottom, 0, HEIGHT);

    ctx.strokeStyle = VIEWPORT_STROKE;
    ctx.lineWidth = 1;
    ctx.strokeRect(x0 + 0.5, y0 + 0.5, Math.max(1, x1 - x0 - 1), Math.max(1, y1 - y0 - 1));
  }

  /**
   * Fit the map's pixel bounds into the panel.
   *
   * Keyed off the map's identity rather than recomputed per update: the client
   * rebuilds `MapState` on every delta, so an identity check alone would redo an
   * O(hexes) bounds pass ten times a second for a layout that never moves.
   */
  private ensureProjection(map: MapState): void {
    const key = `${map.seed}/${map.width}x${map.height}/${map.hexes.length}`;
    if (key === this.projectionKey && this.projection !== null) return;
    this.projectionKey = key;

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    // Bounds at unit size, so the result scales linearly to whatever size fits.
    for (const hex of map.hexes) {
      const p = hexToPixel(hex.coord, 1);
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }

    if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
      this.projection = null;
      return;
    }

    // Half a cell of margin on each side, since bounds are hex centres.
    const spanX = maxX - minX + SQRT3;
    const spanY = maxY - minY + 2;
    const size = Math.min((WIDTH - PADDING * 2) / spanX, (HEIGHT - PADDING * 2) / spanY);

    this.projection = {
      size,
      offsetX: (WIDTH - spanX * size) / 2 - (minX - SQRT3 / 2) * size,
      offsetY: (HEIGHT - spanY * size) / 2 - (minY - 1) * size,
    };
    this.ownershipHash = Number.NaN; // Force a repaint at the new scale.
  }

  // -------------------------------------------------------------------------
  // Click to jump
  // -------------------------------------------------------------------------

  private readonly handlePointerDown = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    event.preventDefault();
    this.dragging = true;
    this.canvas.setPointerCapture(event.pointerId);
    this.jumpTo(event);
  };

  /** Dragging scrubs the camera, which is how every RTS minimap behaves. */
  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (!this.dragging) return;
    this.jumpTo(event);
  };

  private readonly handlePointerUp = (event: PointerEvent): void => {
    if (!this.dragging) return;
    this.dragging = false;
    if (this.canvas.hasPointerCapture(event.pointerId)) {
      this.canvas.releasePointerCapture(event.pointerId);
    }
  };

  private jumpTo(event: PointerEvent): void {
    const projection = this.projection;
    if (projection === null || this.hexSize === null) return;

    const rect = this.canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    // Via CSS pixels, so a scaled or zoomed panel still maps correctly.
    const miniX = ((event.clientX - rect.left) / rect.width) * WIDTH;
    const miniY = ((event.clientY - rect.top) / rect.height) * HEIGHT;

    const scale = this.hexSize / projection.size;
    this.callbacks.onJumpToWorld({
      x: (miniX - projection.offsetX) * scale,
      y: (miniY - projection.offsetY) * scale,
    });
  }
}

function ownershipHash(map: MapState, kingdoms: readonly KingdomState[]): number {
  // Ownership only: troop counts and capture meters change every tick and are
  // not drawn here, so folding them in would defeat the whole point.
  let hash = (map.hexes.length * 31 + kingdoms.length) | 0;

  const index = new Map<KingdomId, number>();
  for (const kingdom of kingdoms) {
    index.set(kingdom.id, index.size + 1);
    hash = (hash * 31 + kingdom.color) | 0;
  }

  for (const hex of map.hexes) {
    const owner = hex.owner === null ? 0 : index.get(hex.owner) ?? 255;
    hash = (hash * 31 + owner) | 0;
  }
  return hash;
}

function clampTo(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return value < min ? min : value > max ? max : value;
}

function preventDefault(event: Event): void {
  event.preventDefault();
}
